import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Target, TrendingUp, Rocket, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function StrategySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  // Parallax scroll effect
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const pillars = [
    { icon: Target, label: t('strategy.pillar1'), color: '#8DC21F' },
    { icon: TrendingUp, label: t('strategy.pillar2'), color: '#5A8C1A' },
    { icon: Rocket, label: t('strategy.pillar3'), color: '#8DC21F' },
    { icon: Zap, label: t('strategy.pillar4'), color: '#f97316' },
  ];

  return (
    <section id="estrategia" ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      {/* Animated background elements */}
      <motion.div style={{ y, opacity }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-1/4 w-64 h-64 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #8DC21F 0%, transparent 70%)' }} />
        <div className="absolute bottom-20 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ background: 'radial-gradient(circle, #5A8C1A 0%, transparent 70%)' }} />
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="v-tag green mb-6 inline-flex">{t('strategy.tag')}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t('strategy.h1a')}{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}>
              {t('strategy.h1b')}
            </span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t('strategy.sub')}
          </p>
        </motion.div>

        {/* Pillars grid with stagger animation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                whileHover={{ y: -8, scale: 1.05 }}
                className="liquid-glass rounded-2xl p-6 text-center group cursor-pointer relative overflow-hidden"
              >
                {/* Hover gradient overlay */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: `linear-gradient(135deg, ${pillar.color}08 0%, transparent 100%)` }}
                />
                
                <div className="relative z-10">
                  <motion.div
                    className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                    style={{ background: `${pillar.color}15`, border: `1px solid ${pillar.color}30` }}
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Icon size={26} style={{ color: pillar.color }} />
                  </motion.div>
                  <p className="text-sm font-semibold text-black dark:text-white/85">{pillar.label}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Highlights with animated counters */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="liquid-glass rounded-3xl p-8 sm:p-10 relative overflow-hidden"
        >
          {/* Decorative gradient */}
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl opacity-5" style={{ background: '#8DC21F' }} />
          
          <h3 className="text-lg font-bold text-black dark:text-white/90 mb-6 relative z-10">{t('strategy.highlightsTitle')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 relative z-10">
            {[
              { v: '19', l: t('strategy.stat1') },
              { v: '10', l: t('strategy.stat2') },
              { v: '8', l: t('strategy.stat3') },
              { v: '11', l: t('strategy.stat4') },
              { v: '+3M€', l: t('strategy.stat5') },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={inView ? { scale: 1, opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                whileHover={{ scale: 1.1 }}
                className="text-center"
              >
                <motion.div
                  className="text-3xl font-bold text-[#8DC21F] mb-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.8, delay: 0.6 + i * 0.1 }}
                >
                  {stat.v}
                </motion.div>
                <div className="text-xs text-gray-600 dark:text-white/50 uppercase tracking-wide">{stat.l}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
